/**
 * Side-by-side model-comparison eval for a single video.
 *
 * Two columns, each with per-phase model overrides (triage,
 * specialists, finalize). Click "Run" on either column to fire a
 * pipeline eval with those overrides; results render in the column
 * below. Running both sides in parallel is easy (click both Runs);
 * results come back independently.
 *
 * Overrides map 1:1 to the backend `AdminEvalPipelineInput`. An
 * empty / unset picker means "use the feature's configured default
 * model" — so leaving all three empty runs the exact canonical
 * pipeline (useful as the "A" baseline).
 *
 * Eval runs don't touch `agent_scores` — results live in state on
 * this panel. The cache_llm log DOES get written (for cost audit),
 * which is desirable.
 */
import React, {useEffect, useState} from 'react';
import {
  ApolloClient,
  NormalizedCacheObject,
} from '@apollo/client';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScienceIcon from '@mui/icons-material/Science';
import {
  AdminEvalPipelineResult,
  AdminEvalRunStatus,
  OpenRouterModelEntry,
  VideoRating,
} from '../../types/generated/graphql';
import {pcolors, cardSx, sectionLabelSx} from '../../theme/planapusTheme';
import {
  useAdminRunPipelineEval,
  useGetPipelineEvalRun,
} from '../../api/graphql_eval_pipeline';
import {useListOpenRouterModels} from '../../api/graphql_evals';
import {ModelPicker} from '../Admin/ModelPicker';

// Per-phase default model — surfaces in the "(use default)" sentinel
// so admins know what they're falling back to when they leave a
// picker empty. Mirrors `featureForAgent` in scoring-service.ts:
//   triage       → Flash-Lite
//   detectors    → Flash-Lite (DETECTION-type Stage 1 specialists)
//   analyzers    → Gemma-4-31B-IT (ANALYSIS-type Stage 1 specialists)
//   finalize     → Gemma-4-31B-IT
// Detector and analyzer pickers are kept separate so the eval lab can
// A/B the cheap detector path against the heavier analyzer path
// independently — the production routing splits them, and merging
// them in the lab made it impossible to compare like-for-like.
const PHASE_DEFAULTS: Record<'triage' | 'specialist' | 'detector' | 'finalize', string> = {
  triage: 'google/gemini-2.5-flash-lite',
  specialist: 'google/gemma-4-31b-it',
  detector: 'google/gemini-2.5-flash-lite',
  finalize: 'google/gemma-4-31b-it',
};

const DEFAULT_SENTINEL_ID = '';

/**
 * Build the model catalogue passed to ModelPicker for one phase.
 * Prepends a synthetic "(default — <model id>)" entry so leaving the
 * picker at default is a first-class option in the dropdown — picker
 * type-search and price-display work uniformly across the rest of the
 * list, while the sentinel surfaces what default actually means for
 * that phase.
 */
function buildPickerModels(
  catalogue: OpenRouterModelEntry[],
  defaultModelId: string,
): OpenRouterModelEntry[] {
  const defaultEntry = catalogue.find(m => m.id === defaultModelId);
  const sentinel: OpenRouterModelEntry = {
    __typename: 'OpenRouterModelEntry',
    id: DEFAULT_SENTINEL_ID,
    name: `(use default — ${defaultModelId})`,
    provider: 'default',
    inputPer1M: defaultEntry?.inputPer1M ?? null,
    outputPer1M: defaultEntry?.outputPer1M ?? null,
    contextLength: defaultEntry?.contextLength ?? null,
  };
  return [sentinel, ...catalogue];
}

interface Overrides {
  triageModel: string;
  // Override for ANALYSIS-type Stage 1 specialists (developmental,
  // engagement, etc.). Field name kept as `specialistModel` for
  // compatibility with the existing GraphQL input — the UI surfaces
  // it as "Analyzers".
  specialistModel: string;
  // Override for DETECTION-type Stage 1 specialists (profanity,
  // religion, politics). New, mirrors the production two-tier split.
  detectorModel: string;
  finalizeModel: string;
}

const EMPTY_OVERRIDES: Overrides = {
  triageModel: '',
  specialistModel: '',
  detectorModel: '',
  finalizeModel: '',
};

function verdictColor(rating: VideoRating | null | undefined) {
  if (rating === VideoRating.Harmful) return {bg: pcolors.red[50], fg: pcolors.red[700]};
  if (rating === VideoRating.Beneficial) return {bg: pcolors.green[50], fg: pcolors.green[700]};
  if (rating === VideoRating.Neutral) return {bg: pcolors.amber[50], fg: pcolors.amber[700]};
  return {bg: pcolors.borderLight, fg: pcolors.textSecondary};
}

// ---- Per-column UI -------------------------------------------------------

interface ColumnProps {
  videoId: string;
  label: string;
  client: ApolloClient<NormalizedCacheObject>;
  /** OpenRouter model catalogue (loaded once at the panel level). */
  models: OpenRouterModelEntry[];
}

function EvalColumn({videoId, label, client, models}: ColumnProps) {
  const [overrides, setOverrides] = useState<Overrides>(EMPTY_OVERRIDES);
  const [evalRunId, setEvalRunId] = useState<string | null>(null);
  const [enqueueError, setEnqueueError] = useState<string | null>(null);
  const [runEval, {loading: enqueuing}] = useAdminRunPipelineEval(client);
  const {data: runData, startPolling, stopPolling} = useGetPipelineEvalRun(client, evalRunId);
  const run = runData?.getPipelineEvalRun ?? null;

  // Poll while the worker is still processing; stop once terminal so
  // we don't burn Firestore reads on finished runs.
  useEffect(() => {
    if (!evalRunId) return;
    if (!run || run.status === AdminEvalRunStatus.Pending || run.status === AdminEvalRunStatus.Running) {
      startPolling(2000);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [evalRunId, run?.status, startPolling, stopPolling]);

  const onModelChange = (key: keyof Overrides) => (id: string) => {
    setOverrides(prev => ({...prev, [key]: id}));
  };

  // Each phase's picker draws from the same OpenRouter catalogue with
  // a phase-specific "(use default)" sentinel prepended. Memoization
  // would be nice; the catalogue is small (~350 models) so building
  // these three arrays per render is cheap.
  const triageModels = buildPickerModels(models, PHASE_DEFAULTS.triage);
  const specialistModels = buildPickerModels(models, PHASE_DEFAULTS.specialist);
  const detectorModels = buildPickerModels(models, PHASE_DEFAULTS.detector);
  const finalizeModels = buildPickerModels(models, PHASE_DEFAULTS.finalize);

  const onRun = async () => {
    setEnqueueError(null);
    setEvalRunId(null);
    try {
      const {data} = await runEval({
        variables: {
          input: {
            videoId,
            triageModel: overrides.triageModel || null,
            specialistModel: overrides.specialistModel || null,
            detectorModel: overrides.detectorModel || null,
            finalizeModel: overrides.finalizeModel || null,
          },
        },
      });
      const newId = data?.adminRunPipelineEval?.evalRunId ?? null;
      if (newId) setEvalRunId(newId);
    } catch (err) {
      setEnqueueError(err instanceof Error ? err.message : String(err));
    }
  };

  // Derived UI state. "Running" means the worker is actively
  // processing — shown with a spinner + elapsed timer.
  const isRunning =
    !!evalRunId &&
    (!run || run.status === AdminEvalRunStatus.Pending || run.status === AdminEvalRunStatus.Running);
  const isCompleted = run?.status === AdminEvalRunStatus.Completed;
  const isFailed = run?.status === AdminEvalRunStatus.Failed;

  return (
    <Paper sx={{...cardSx, p: 2, flex: 1, minWidth: 0}}>
      <Typography sx={{...sectionLabelSx, mb: 1.5}}>{label}</Typography>

      {/* Per-phase model pickers — searchable, with inline pricing.
          Reuses the same ModelPicker the AdminAgentsLab eval-runner
          uses, fed from the live OpenRouter catalogue. */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2}}>
        <ModelPicker
          models={triageModels}
          value={overrides.triageModel}
          onChange={onModelChange('triageModel')}
          label="Triage"
          disabled={isRunning}
        />
        <ModelPicker
          models={detectorModels}
          value={overrides.detectorModel}
          onChange={onModelChange('detectorModel')}
          label="Detectors (profanity / religion / politics)"
          disabled={isRunning}
        />
        <ModelPicker
          models={specialistModels}
          value={overrides.specialistModel}
          onChange={onModelChange('specialistModel')}
          label="Analyzers (developmental / engagement / …)"
          disabled={isRunning}
        />
        <ModelPicker
          models={finalizeModels}
          value={overrides.finalizeModel}
          onChange={onModelChange('finalizeModel')}
          label="Finalize"
          disabled={isRunning}
        />
      </Box>

      <Box sx={{display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap'}}>
        <Button
          size="small"
          variant="contained"
          onClick={onRun}
          disabled={enqueuing || isRunning}
          sx={{textTransform: 'none'}}
        >
          {enqueuing ? 'Enqueuing…' : isRunning ? 'Running in background…' : 'Run'}
        </Button>
        {(enqueuing || isRunning) && <CircularProgress size={16} />}
        {run && (
          <RunStatusChip
            status={run.status}
            elapsed={computeElapsed(run)}
            progress={run.progress}
          />
        )}
        {isCompleted && (
          <Typography
            sx={{fontSize: 11, color: pcolors.textMuted, fontVariantNumeric: 'tabular-nums'}}
          >
            {run.totalTokens.toLocaleString()} tok · ${run.totalCostUSD.toFixed(4)}
          </Typography>
        )}
      </Box>

      {enqueueError && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: pcolors.red[50],
            color: pcolors.red[700],
            fontSize: 12,
            border: `1px solid ${pcolors.red[100]}`,
          }}
        >
          Enqueue failed: {enqueueError}
        </Paper>
      )}

      {isFailed && run.error && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: pcolors.red[50],
            color: pcolors.red[700],
            fontSize: 12,
            border: `1px solid ${pcolors.red[100]}`,
          }}
        >
          Run failed: {run.error}
        </Paper>
      )}

      {/* Render the result view as soon as ANY partial lands — triage,
          a few specialists, etc. — instead of gating on isCompleted.
          The worker writes progressive snapshots to the eval_run doc
          during the run, so fields fill in incrementally. Completed
          and in-flight runs share the same renderer; the chip overhead
          shows which phase is still pending. */}
      {run && (run.triage || run.specialists.length > 0 || run.finalize) && (
        <EvalResultView result={run as AdminEvalPipelineResult} />
      )}
    </Paper>
  );
}

function computeElapsed(run: {enqueuedAt: string; startedAt?: string | null; finishedAt?: string | null}): string {
  const start = Date.parse(run.startedAt ?? run.enqueuedAt);
  const end = run.finishedAt ? Date.parse(run.finishedAt) : Date.now();
  const sec = Math.max(0, Math.round((end - start) / 1000));
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

function RunStatusChip({
  status,
  elapsed,
  progress,
}: {
  status: AdminEvalRunStatus;
  elapsed: string;
  progress?: {phase: string; completed?: number | null; total?: number | null; note?: string | null} | null;
}) {
  // Build a human-readable progress suffix when the run is RUNNING and
  // the worker has reported a phase. Specialists phase additionally
  // shows the count so admins can see incremental progress.
  const progressLabel = progress
    ? progress.phase === 'specialists' && progress.total != null
      ? `specialists ${progress.completed ?? 0}/${progress.total}`
      : progress.phase
    : null;
  const tone = (() => {
    switch (status) {
      case AdminEvalRunStatus.Pending:
        return {bg: pcolors.amber[50], fg: pcolors.amber[700], label: `queued · ${elapsed}`};
      case AdminEvalRunStatus.Running:
        return {
          bg: pcolors.blue[50],
          fg: pcolors.blue[700],
          label: progressLabel
            ? `${progressLabel} · ${elapsed}`
            : `running · ${elapsed}`,
        };
      case AdminEvalRunStatus.Completed:
        return {bg: pcolors.green[50], fg: pcolors.green[700], label: `completed · ${elapsed}`};
      case AdminEvalRunStatus.Failed:
        return {bg: pcolors.red[50], fg: pcolors.red[700], label: `failed · ${elapsed}`};
      default:
        return {bg: pcolors.borderLight, fg: pcolors.textSecondary, label: status};
    }
  })();
  // Tooltip shows the most-recent specialist that finished so admins
  // can see the runner is making progress on the parallel fan-out
  // even when the count itself isn't moving.
  const tooltip = progress?.note ? `last: ${progress.note.toLowerCase().replace(/_/g, ' ')}` : '';
  const chip = (
    <Chip
      size="small"
      label={tone.label}
      sx={{
        fontSize: 10,
        fontWeight: 700,
        bgcolor: tone.bg,
        color: tone.fg,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    />
  );
  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
}

// ---- Result renderer ------------------------------------------------------

function EvalResultView({result}: {result: AdminEvalPipelineResult}) {
  return (
    <Box>
      {/* Triage */}
      {result.triage && (
        <Section title="Triage" model={result.triage.model}>
          <ScoreRatingLine
            score={result.triage.score}
            rating={result.triage.rating}
          />
          <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5}}>
            <Chip
              size="small"
              label={`verdict ${result.triage.verdict.toLowerCase()} · conf ${result.triage.confidence.toFixed(2)}`}
              sx={{fontSize: 10, bgcolor: pcolors.borderLight}}
            />
            {result.triage.categoryTags.map(t => (
              <Chip
                key={`tag-${t}`}
                size="small"
                label={t.toLowerCase()}
                sx={{fontSize: 10, bgcolor: pcolors.blue[50], color: pcolors.blue[700]}}
              />
            ))}
            {result.triage.riskFlags.map(f => (
              <Chip
                key={`flag-${f}`}
                size="small"
                label={f.replace(/^POSSIBLE_/, '').toLowerCase().replace(/_/g, ' ')}
                sx={{fontSize: 10, bgcolor: pcolors.amber[50], color: pcolors.amber[700]}}
              />
            ))}
          </Box>
          <Typography sx={{fontSize: 12, color: pcolors.textSecondary, mt: 1, lineHeight: 1.5}}>
            {result.triage.explanation}
          </Typography>
        </Section>
      )}

      {/* Specialists — alphabetical by specialization so rows line up
          across the two comparison columns and stay put as partials
          stream in. Without this, results would render in
          completion order, which is non-deterministic under parallel
          fan-out and makes side-by-side diffs hard to read. */}
      {(() => {
        const orderedSpecialists = [...result.specialists].sort((a, b) =>
          a.specialization.localeCompare(b.specialization),
        );
        // Distinct models across this run's specialists. Triage can
        // downgrade an ANALYSIS specialist to Flash-Lite for benign
        // content (see scoring-service.ts:417), so a single run
        // legitimately produces specialists on two different models.
        // Showing only the first row's model — what we used to do —
        // hid that.
        const distinctModels = Array.from(
          new Set(orderedSpecialists.map(s => s.model).filter(Boolean)),
        );
        const headerModel =
          distinctModels.length === 0
            ? '—'
            : distinctModels.length === 1
              ? distinctModels[0]
              : `${distinctModels.length} models`;
        return (
          <Section
            title={`Specialists (${orderedSpecialists.length})`}
            model={headerModel}
          >
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
              {orderedSpecialists.map(s => (
            <Box
              key={s.specialization}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 200px) auto minmax(0, 140px) 1fr',
                gap: 0.75,
                alignItems: 'baseline',
                py: 0.5,
                borderBottom: `1px solid ${pcolors.borderLight}`,
                '&:last-of-type': {borderBottom: 'none'},
              }}
            >
              <Typography sx={{fontSize: 11, fontWeight: 600, color: pcolors.textPrimary}}>
                {s.specialization.replace(/_/g, ' ').toLowerCase()}
              </Typography>
              <ScoreRatingLine score={s.score} rating={s.rating} compact />
              <Typography
                sx={{
                  fontSize: 10,
                  fontFamily: 'monospace',
                  color: pcolors.textMuted,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={s.model}
              >
                {s.model}
              </Typography>
              <Typography sx={{fontSize: 11, color: pcolors.textSecondary, lineHeight: 1.45}}>
                {s.explanation}
              </Typography>
            </Box>
          ))}
          {result.skippedSpecialists.length > 0 && (
            <Typography
              sx={{fontSize: 10.5, color: pcolors.textMuted, mt: 0.5, fontStyle: 'italic'}}
            >
              Skipped by triage: {result.skippedSpecialists.length} agent{result.skippedSpecialists.length === 1 ? '' : 's'}
              {' '}
              ({result.skippedSpecialists.map(s => s.specialization.toLowerCase()).join(', ')})
            </Typography>
          )}
          {result.failedSpecialists.length > 0 && (
            <Box sx={{mt: 0.75}}>
              <Typography sx={{fontSize: 10.5, fontWeight: 700, color: pcolors.red[700]}}>
                Failed at run time: {result.failedSpecialists.length} agent{result.failedSpecialists.length === 1 ? '' : 's'}
              </Typography>
              {result.failedSpecialists.map(f => (
                <Typography
                  key={f.specialization}
                  sx={{fontSize: 10.5, color: pcolors.red[700], lineHeight: 1.4, mt: 0.25}}
                >
                  • {f.specialization.toLowerCase().replace(/_/g, ' ')}: {f.error}
                </Typography>
              ))}
            </Box>
          )}
            </Box>
          </Section>
        );
      })()}

      {/* Finalize */}
      {result.finalize && (
        <Section title="Finalize (aggregate + age bands)" model={result.finalize.model}>
          <ScoreRatingLine
            score={result.finalize.score}
            rating={result.finalize.rating}
          />
          <Typography sx={{fontSize: 12, color: pcolors.textSecondary, mt: 0.5, lineHeight: 1.5}}>
            {result.finalize.explanation}
          </Typography>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1}}>
            {result.finalize.ageBandVerdicts.map(b => {
              const tone = verdictColor(b.rating);
              return (
                <Box
                  key={b.band}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '80px 90px 1fr',
                    gap: 0.75,
                    alignItems: 'center',
                    py: 0.5,
                  }}
                >
                  <Chip
                    size="small"
                    label={`${b.band} ${b.minAge}–${b.maxAge}`}
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: pcolors.borderLight,
                      color: pcolors.textSecondary,
                    }}
                  />
                  <Chip
                    size="small"
                    label={`${b.score.toFixed(1)} · ${b.rating.toLowerCase()}`}
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: tone.bg,
                      color: tone.fg,
                      textTransform: 'capitalize',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  />
                  <Typography sx={{fontSize: 11, color: pcolors.textSecondary, lineHeight: 1.4}}>
                    {b.explanation}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Section>
      )}
    </Box>
  );
}

function Section({
  title,
  model,
  children,
}: {
  title: string;
  model: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{mb: 2}}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
        <Typography sx={{fontSize: 11, fontWeight: 700, color: pcolors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4}}>
          {title}
        </Typography>
        <Chip
          size="small"
          label={model}
          sx={{fontSize: 10, fontFamily: 'monospace', bgcolor: pcolors.borderLight, color: pcolors.textMuted}}
        />
      </Box>
      <Divider sx={{mb: 1}} />
      {children}
    </Box>
  );
}

function ScoreRatingLine({
  score,
  rating,
  compact = false,
}: {
  score: number;
  rating: VideoRating;
  compact?: boolean;
}) {
  const tone = verdictColor(rating);
  return (
    <Box sx={{display: 'flex', gap: 0.5, alignItems: 'center'}}>
      <Chip
        size="small"
        label={`${score.toFixed(1)}/10`}
        sx={{
          fontSize: compact ? 10 : 11,
          fontWeight: 700,
          bgcolor: tone.bg,
          color: tone.fg,
          fontVariantNumeric: 'tabular-nums',
        }}
      />
      <Chip
        size="small"
        label={rating.toLowerCase()}
        sx={{
          fontSize: compact ? 10 : 11,
          fontWeight: 600,
          textTransform: 'capitalize',
          bgcolor: tone.bg,
          color: tone.fg,
        }}
      />
    </Box>
  );
}

// ---- Outer accordion ------------------------------------------------------

interface Props {
  videoId: string;
  client: ApolloClient<NormalizedCacheObject>;
}

export function EvalLabPanel({videoId, client}: Props) {
  // Load the OpenRouter catalogue once, share across both columns.
  // Uses cache-first inside the hook; subsequent panel mounts are a
  // no-op network-wise.
  const {data: modelsData, loading: modelsLoading} = useListOpenRouterModels(client);
  const models: OpenRouterModelEntry[] = modelsData?.listOpenRouterModels ?? [];

  return (
    <Accordion sx={{...cardSx, mb: 3, '&:before': {display: 'none'}}}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <ScienceIcon sx={{fontSize: 18, color: pcolors.textSecondary}} />
          <Typography sx={sectionLabelSx}>Eval Lab — model comparison</Typography>
          <Tooltip title="Run the full pipeline twice with different model configs side-by-side. Does NOT write agent_scores; LLM calls are logged in cache_llm for cost audit.">
            <Chip
              size="small"
              label="admin · non-persistent"
              sx={{fontSize: 10, ml: 1, bgcolor: pcolors.blue[50], color: pcolors.blue[700]}}
            />
          </Tooltip>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {modelsLoading && models.length === 0 ? (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, py: 2}}>
            <CircularProgress size={16} />
            <Typography sx={{fontSize: 13, color: pcolors.textSecondary}}>
              Loading OpenRouter model catalogue…
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: {xs: 'column', md: 'row'},
              alignItems: 'stretch',
            }}
          >
            <EvalColumn videoId={videoId} label="A — baseline" client={client} models={models} />
            <EvalColumn videoId={videoId} label="B — comparison" client={client} models={models} />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
